import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table'
import { truncateToMinuteFloor, minutesBetween } from '@/lib/dateUtils'
import type { RecoveryRunbookTask } from '@/types/entities'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { Button } from '@/components/ui/button'
import { Check, Play, Copy, Wand2, Bot, MessageSquarePlus, MessageSquareText } from 'lucide-react'
import { EditableStatusCell, EditableOwnerCell, ReadOnlyOwnerDisplay, createRunbookRecoveryColumns, computeForecastTimesMap, DropdownOpenRowIndexContext, ExpandedRowsContext } from './columns-runbook-recovery'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { CardTableViewToggle } from '@/components/ui/card-table-view-toggle'
import { RecoveryOptionsPanel } from './RecoveryOptionsPanel'
import { VersionUrlsTable } from './VersionUrlsTable'
import { AutomateTaskDialog } from './AutomateTaskDialog'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { useCan } from '@/hooks/useCan'

interface RunbookRecoveryTableProps {
    tasks: RecoveryRunbookTask[]
    forecastStartTime: Date | null
    onSave: (tasks: RecoveryRunbookTask[]) => void
    requestID: number | string
    onPauseRefetchChange?: (pause: boolean) => void
    readOnly?: boolean
    onTaskIssue?: (task: RecoveryRunbookTask) => void
    /** Opens the per-task comment dialog. Absent in read-only views. */
    onComment?: (task: RecoveryRunbookTask) => void
}

type TaskStatusDisplay = 'complete' | 'inProgress' | 'waiting' | 'ready' | 'issue'

function getTaskStatusDisplay(task: RecoveryRunbookTask): TaskStatusDisplay {
    const status = (task.taskStatus || 'Pending').toLowerCase()
    const waitGo = (task.waitGo || 'WAIT').toUpperCase()

    if (status === 'completed') return 'complete'
    if (status === 'started') return 'inProgress'
    if (status === 'issue') return 'issue'
    if (status === 'pending' && waitGo === 'WAIT') return 'waiting'
    if (status === 'pending' && waitGo === 'GO') return 'ready'
    return 'ready'
}

type AutomationMode = 'assisted' | 'agent'

/** The automation tier if a script or the restore agent performs this task, else null. */
function taskAutomation(task: RecoveryRunbookTask): AutomationMode | null {
    const m = (task.mode || 'manual').toLowerCase()
    return m === 'assisted' || m === 'agent' ? (m as AutomationMode) : null
}

/**
 * "Armed" = the task has an assisted/automated script AND its dependencies are met
 * (WaitGo === GO) AND it isn't finished — ready for the engineer to review and fire.
 * A WAIT task is not yet armed.
 */
function isArmed(task: RecoveryRunbookTask): boolean {
    if (!taskAutomation(task)) return false
    const status = (task.taskStatus || 'Pending').toLowerCase()
    return (task.waitGo || 'WAIT').toUpperCase() === 'GO' && status !== 'completed'
}

/** Chip on a task: which automation tier it is, and whether it's armed. */
function AutomationBadge({ task }: { task: RecoveryRunbookTask }) {
    const { t } = useTranslation()
    const mode = taskAutomation(task)
    if (!mode) return null
    const armed = isArmed(task)
    const Icon = mode === 'agent' ? Bot : Wand2
    const label = mode === 'agent' ? t('objects.runbook.modeAgent') : t('objects.runbook.modeAssisted')
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                armed ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-300' : 'bg-gray-100 text-gray-500'
            }`}
            title={armed
                ? `${label} script — armed (dependencies met, ready to run)`
                : `${label} script — waiting for dependencies`}
        >
            <Icon className="h-3 w-3" />
            {label}{armed ? ' · armed' : ''}
        </span>
    )
}

function ForecastIndicator({
    task,
    forecastFinishMs,
    t,
}: {
    task: RecoveryRunbookTask
    forecastFinishMs: number | null
    t: TFunction
}) {
    const plannedFinish = task.plannedFinish ? new Date(task.plannedFinish) : null
    const forecastFinish = forecastFinishMs != null ? new Date(forecastFinishMs) : null

    if (!plannedFinish || !forecastFinish) {
        return (
            <div className="w-[100px] flex-shrink-0 text-right">
                <span className="text-xs text-gray-400">-</span>
            </div>
        )
    }

    const deviationMinutes = Math.round(
        (plannedFinish.getTime() - forecastFinish.getTime()) / 60000
    )
    const isAhead = deviationMinutes > 0
    const isBehind = deviationMinutes < 0
    const deviationFormatted = formatSecondsToDHM(Math.abs(deviationMinutes) * 60)

    const label =
        deviationMinutes === 0
            ? t('recoverRequests.forecastOnSchedule')
            : isAhead
              ? t('recoverRequests.forecastAhead', { time: deviationFormatted })
              : t('recoverRequests.forecastBehind', { time: deviationFormatted })

    const isCompleted = !!task.actualFinish
    const tooltip =
        deviationMinutes === 0
            ? isCompleted
                ? t('recoverRequests.finishedOnSchedule')
                : t('recoverRequests.expectedOnSchedule')
            : isCompleted
              ? isAhead
                  ? t('recoverRequests.finishedAhead', { time: deviationFormatted })
                  : t('recoverRequests.finishedBehind', { time: deviationFormatted })
              : isAhead
                ? t('recoverRequests.expectedAhead', { time: deviationFormatted })
                : t('recoverRequests.expectedBehind', { time: deviationFormatted })

    const clampRange = 60
    const clampedDev = Math.max(-clampRange, Math.min(clampRange, deviationMinutes))
    const positionPercent = Math.min(100, Math.max(0, 50 + (clampedDev / clampRange) * 50))

    return (
        <div
            className="w-[100px] flex-shrink-0 flex flex-col items-end gap-0.5"
            title={tooltip}
        >
            <span
                className={`text-xs font-medium ${
                    isAhead ? 'text-emerald-600' : isBehind ? 'text-red-500' : 'text-gray-500'
                }`}
            >
                {label}
            </span>
            <div className="relative h-1.5 w-full max-w-[80px] rounded-full bg-gray-200 overflow-visible">
                <div
                    className="absolute top-0 left-1/2 w-px h-full bg-gray-400 -translate-x-px"
                    aria-hidden
                />
                <div
                    className={`absolute top-1/2 h-2 w-2 rounded-full -translate-y-1/2 -translate-x-1/2 ${
                        isAhead ? 'bg-emerald-500' : isBehind ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ left: `${positionPercent}%` }}
                />
            </div>
        </div>
    )
}

function TimelineNode({
    task,
    index,
    onTaskChange,
    t,
    readOnly = false,
}: {
    task: RecoveryRunbookTask
    index: number
    onTaskChange: (index: number, field: keyof RecoveryRunbookTask, value: any) => void
    t: TFunction
    readOnly?: boolean
}) {
    const display = getTaskStatusDisplay(task)
    const taskNo = task.taskNo ?? '-'
    const isReady = display === 'ready' && !readOnly

    // Solid status colours — fully cover the timeline line behind the node and keep the recovery
    // status prominent (green complete, blue in-progress, amber waiting, purple ready, red issue).
    const nodeClasses = {
        complete: 'bg-emerald-500 text-white',
        inProgress: 'bg-blue-500 text-white ring-4 ring-blue-100 ring-offset-2',
        waiting: 'bg-amber-500 text-white',
        ready: 'bg-purple-500 text-white animate-runbook-ready-ring',
        issue: 'bg-red-500 text-white',
    }

    const handleNodeClick = (e: React.MouseEvent) => {
        if (!isReady) return
        e.stopPropagation()
        onTaskChange(index, 'taskStatus', 'Started')
    }

    return (
        <div
            role={isReady ? 'button' : undefined}
            tabIndex={isReady ? 0 : undefined}
            onClick={handleNodeClick}
            onKeyDown={isReady ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTaskChange(index, 'taskStatus', 'Started') } } : undefined}
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-transform ${nodeClasses[display]} ${isReady ? 'cursor-pointer hover:scale-110' : ''}`}
            aria-label={t('objects.runbook.taskNo') + ' ' + taskNo}
        >
            {display === 'complete' ? (
                <Check className="h-5 w-5" aria-hidden />
            ) : display === 'ready' ? (
                <span className="animate-runbook-ready-pulse inline-flex">
                    <Play className="h-5 w-5 ml-0.5" aria-hidden fill="currentColor" />
                </span>
            ) : (
                <span>{taskNo}</span>
            )}
        </div>
    )
}

function parseDependencyNumbers(deps: string): number[] {
    if (!deps?.trim()) return []
    return deps
        .split(/[,\s]+/)
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n > 0)
}

function TaskCard({
    task,
    index,
    isExpanded,
    onExpand,
    onTaskChange,
    onAutomate,
    onDropdownOpenChange,
    forecastFinishMs,
    copyToClipboard,
    copiedId,
    allTasks,
    t,
    readOnly = false,
    onComment,
}: {
    task: RecoveryRunbookTask & { _index: number }
    index: number
    isExpanded: boolean
    onExpand: () => void
    onTaskChange: (index: number, field: keyof RecoveryRunbookTask, value: any) => void
    onAutomate?: (task: RecoveryRunbookTask) => void
    onDropdownOpenChange: (open: boolean, rowIndex?: number) => void
    onComment?: (task: RecoveryRunbookTask) => void
    forecastFinishMs: number | null
    copyToClipboard: (text: string, id: string) => void
    copiedId: string | null
    allTasks: (RecoveryRunbookTask & { _index: number })[]
    t: TFunction
    readOnly?: boolean
}) {
    const display = getTaskStatusDisplay(task)

    const cardClasses = {
        complete: 'bg-white border-2 border-emerald-200',
        inProgress: 'bg-white border-2 border-blue-300 shadow-lg shadow-blue-100',
        waiting: 'bg-gray-50 border-2 border-gray-200 opacity-75',
        ready: 'bg-white border-2 border-purple-400 shadow-md shadow-purple-100',
        issue: 'bg-white border-2 border-red-200',
    }

    const statusBadgeClasses = {
        complete: 'bg-emerald-100 text-emerald-700',
        inProgress: 'bg-blue-100 text-blue-700',
        waiting: 'bg-amber-200 text-amber-800 font-semibold text-sm px-3 py-1',
        ready: 'bg-purple-100 text-purple-800 font-semibold text-sm px-3 py-1',
        issue: 'bg-red-100 text-red-700',
    }

    const statusLabels: Record<TaskStatusDisplay, string> = {
        complete: t('recoverRequests.taskStatusComplete'),
        inProgress: t('recoverRequests.taskStatusInProgress'),
        waiting: t('recoverRequests.taskStatusWaiting'),
        ready: t('recoverRequests.taskStatusStart'),
        issue: t('recoverRequests.taskStatusIssue'),
    }

    const duration =
        task.duration != null
            ? formatSecondsToDHM(task.duration * 60)
            : '-'

    const handleCardClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button, [role="combobox"], [data-radix-collection-item], [data-copy-btn]'))
            return
        onExpand()
    }

    const taskId = task.id ?? index
    const taskCopyId = `task-${taskId}`
    const commandsCopyId = `commands-${taskId}`

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onExpand()
                }
            }}
            className={`rounded-xl border p-4 transition-shadow hover:shadow-md cursor-pointer ${cardClasses[display]}`}
            aria-expanded={isExpanded}
        >
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusBadgeClasses[display]}`}
                    >
                        {statusLabels[display]}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{duration}</span>
                    <AutomationBadge task={task} />
                </div>
                <ForecastIndicator task={task} forecastFinishMs={forecastFinishMs} t={t} />
            </div>

            <div className="flex items-start gap-2">
                <p
                    className={`text-sm text-foreground flex-1 min-w-0 ${!isExpanded ? 'line-clamp-1' : ''}`}
                    title={task.task || ''}
                >
                    <TranslatableText text={task.task || t('objects.runbook.task')} />
                </p>
                {/* ALWAYS REACHABLE, collapsed or not. An engineer noticing a step is wrong is
                    reading the card, not editing it, and burying the flag behind an expand meant
                    the moment passed before the affordance appeared. A filled icon says a flag
                    already exists here, so the next person can see it without opening anything. */}
                {onComment && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onComment(task) }}
                        className={`flex-shrink-0 p-1 rounded transition-colors ${
                            (task.commentCount ?? 0) > 0
                                ? 'text-amber-600 hover:text-amber-700'
                                : 'text-gray-400 hover:text-gray-600'
                        }`}
                        title={(task.commentCount ?? 0) > 0
                            ? t('postRecovery.flagStepUsed', 'Flagged — open to read or add')
                            : t('postRecovery.flagStep', 'Flag a problem with this step')}
                        aria-label={t('postRecovery.flagStep', 'Flag a problem with this step')}
                    >
                        {(task.commentCount ?? 0) > 0 ? (
                            <span className="flex items-center gap-0.5">
                                <MessageSquareText className="h-4 w-4" />
                                <span className="text-[10px] font-semibold tabular-nums">{task.commentCount}</span>
                            </span>
                        ) : (
                            <MessageSquarePlus className="h-4 w-4" />
                        )}
                    </button>
                )}
                <button
                    type="button"
                    data-copy-btn
                    onClick={(e) => {
                        e.stopPropagation()
                        copyToClipboard(task.task || '', taskCopyId)
                    }}
                    className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 transition-colors"
                    title="Copy task text"
                    aria-label="Copy task text"
                >
                    {copiedId === taskCopyId ? (
                        <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                        <Copy className="h-4 w-4" />
                    )}
                </button>
            </div>

            {!readOnly && isArmed(task) && onAutomate && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        className="border-purple-300 text-purple-700 hover:bg-purple-100"
                        onClick={(e) => { e.stopPropagation(); onAutomate(task) }}
                    >
                        <Play className="h-3.5 w-3.5 mr-1" />
                        {t('objects.runbook.reviewAndRun') || 'Review & run script'}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                        {t('objects.runbook.automationAsyncHint') || 'runs out-of-band — mark complete when finished'}
                    </span>
                </div>
            )}

            {isExpanded && (
                <>
                    <div className="mt-4 pt-4 border-t border-gray-100" />
                    <div className="mt-4 space-y-4">
                        {display === 'waiting' && (() => {
                            const depNos = parseDependencyNumbers(task.dependencies || '')
                            const incompleteDeps = depNos.filter((no) => {
                                const depTask = allTasks.find((a) => Number(a.taskNo) === no)
                                const status = (depTask?.taskStatus || 'Pending').toLowerCase()
                                return status !== 'completed'
                            })
                            if (incompleteDeps.length > 0) {
                                return (
                                    <div className="mb-4">
                                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                                            {t('objects.runbook.dependencies') || 'Dependencies'}
                                        </p>
                                        <p className="text-sm text-amber-700">
                                            {t('objects.runbook.waitingForTasks') || 'Waiting for tasks'}: {incompleteDeps.sort((a, b) => a - b).join(', ')}
                                        </p>
                                    </div>
                                )
                            }
                            return null
                        })()}
                        <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                                {t('objects.runbook.commands') || 'Commands'}
                            </p>
                            {task.commands?.trim() ? (
                                <div className="flex items-start gap-2">
                                    <pre className="flex-1 min-w-0 rounded-lg bg-gray-900 text-gray-100 p-3 font-mono text-sm whitespace-pre-wrap overflow-x-auto">
                                        {task.commands}
                                    </pre>
                                    <button
                                        type="button"
                                        data-copy-btn
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            copyToClipboard(task.commands || '', commandsCopyId)
                                        }}
                                        className="flex-shrink-0 p-2 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                                        title="Copy commands"
                                        aria-label="Copy commands"
                                    >
                                        {copiedId === commandsCopyId ? (
                                            <Check className="h-4 w-4 text-emerald-400" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <p className="text-gray-400 italic text-sm">
                                    {t('objects.runbook.noCommands') || 'No commands for this task'}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                            {readOnly ? (
                                <>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {t('recoverRequests.taskStatus') || 'Status'}:
                                        </span>
                                        <span className="text-xs px-2 py-0.5">
                                            {(task.taskStatus as string) || '—'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {t('recoverRequests.taskOwner') || 'User'}:
                                        </span>
                                        <ReadOnlyOwnerDisplay value={(task.taskOwner as string | null) || null} />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <EditableStatusCell
                                            value={(task.taskStatus as string | null) || null}
                                            onUpdate={(v) => onTaskChange(index, 'taskStatus', v)}
                                            onDropdownOpenChange={(open) =>
                                                onDropdownOpenChange?.(open, index)
                                            }
                                        />
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {t('recoverRequests.taskOwner') || 'User'}:
                                        </span>
                                        <EditableOwnerCell
                                            value={(task.taskOwner as string | null) || null}
                                            onUpdate={(v) => onTaskChange(index, 'taskOwner', v)}
                                            onDropdownOpenChange={(open) =>
                                                onDropdownOpenChange?.(open, index)
                                            }
                                        />
                                    </div>
                                    {/* An engineer standing in front of a step that is wrong is
                                        the most valuable signal a recovery produces, and the one
                                        least likely to survive to the debrief. One click, here,
                                        while they are looking at it. */}
                                    {onComment && (
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 px-2 text-xs"
                                                onClick={() => onComment(task)}
                                            >
                                                <MessageSquarePlus className="h-3.5 w-3.5 mr-1" />
                                                {t('postRecovery.flagStep', 'Flag a problem with this step')}
                                                {(task.commentCount ?? 0) > 0 && (
                                                    <span className="ml-1.5 rounded-full bg-muted px-1.5 tabular-nums">
                                                        {task.commentCount}
                                                    </span>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export function RunbookRecoveryTable({
    tasks,
    forecastStartTime,
    onSave,
    requestID,
    onPauseRefetchChange,
    readOnly = false,
    onTaskIssue,
    onComment,
}: RunbookRecoveryTableProps) {
    const { t, i18n } = useTranslation()
    const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
    const [showFullTasks, setShowFullTasks] = useState(false)
    const [showActualsAndForecast, setShowActualsAndForecast] = useState(true)
    const [localTasks, setLocalTasks] = useState<RecoveryRunbookTask[]>(tasks || [])
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [dropdownOpenRowIndex, setDropdownOpenRowIndex] = useState<number | null>(null)
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const onSaveRef = useRef(onSave)
    onSaveRef.current = onSave
    const hasEditsRef = useRef(false)
    // Post-save re-sync guard. When a save fires we clear dirtyIndices immediately,
    // but the authoritative refetch hasn't landed yet, so `tasks` is still the
    // pre-save snapshot — adopting it would momentarily revert the just-made edit.
    // We hold the local copy from save-fire until a NEW `tasks` reference arrives
    // (that refetch), which also carries the server-recomputed WaitGo for dependent
    // tasks. Data-driven, not a timer: the previous magic-number window dropped the
    // authoritative refetch and left the screen showing stale WAIT/GO.
    const awaitingRefetchRef = useRef(false)
    const preSaveTasksRef = useRef<RecoveryRunbookTask[] | null>(null)
    // Live mirror of the `tasks` prop so the debounced save captures the true
    // pre-save reference at fire time — its own closure would be stale across
    // rapid successive edits and could momentarily revert a just-made change.
    const tasksRef = useRef(tasks)
    tasksRef.current = tasks
    const [dirtyIndices, setDirtyIndices] = useState<Set<number>>(new Set())
    const [copiedId, setCopiedId] = useState<string | null>(null)
    // Arm-not-fire automation dialog: which GO assisted/automated task to preview+run.
    const [automateTask, setAutomateTask] = useState<RecoveryRunbookTask | null>(null)
    const [automateOpen, setAutomateOpen] = useState(false)
    const openAutomate = useCallback((task: RecoveryRunbookTask) => {
        setAutomateTask(task)
        setAutomateOpen(true)
    }, [])
    // Running an automated task is recover-requests.update on the server, not the engineer's
    // recovery.execute: without it the task is worked by hand, and Automate is not offered.
    const canAutomate = useCan('recover-requests.update')

    const copyToClipboard = useCallback(async (text: string, id: string) => {
        await navigator.clipboard.writeText(text)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
    }, [])

    useEffect(() => {
        if (!tasks || tasks.length === 0) return
        // Never overwrite the engineer's in-progress edits.
        if (dirtyIndices.size > 0 || dropdownOpen) return
        if (awaitingRefetchRef.current) {
            // Held since save-fire: `tasks` is still the pre-save snapshot. Adopting
            // it now would flash the just-made edit back to its old value. Wait for
            // the authoritative refetch (a new `tasks` reference) — it carries the
            // edit plus the server-recomputed WaitGo for dependents — then release.
            if (tasks === preSaveTasksRef.current) return
            awaitingRefetchRef.current = false
        }
        setLocalTasks(tasks)
    }, [tasks, dirtyIndices.size, dropdownOpen])

    const handleTaskChange = useCallback((index: number, field: keyof RecoveryRunbookTask, value: any) => {
        if (readOnly) return
        hasEditsRef.current = true
        setDirtyIndices((prev) => new Set(prev).add(index))
        setDropdownOpen(false)
        setDropdownOpenRowIndex(null)
        if (field === 'taskStatus' && value === 'Completed') {
            setExpandedRows((prev) => {
                const next = new Set(prev)
                next.delete(index)
                return next
            })
        }
        setLocalTasks((prev) => {
            const updated = [...prev]
            const task = { ...updated[index], [field]: value } as RecoveryRunbookTask
            if (field === 'taskStatus') {
                const now = truncateToMinuteFloor(new Date())
                // A task that goes STRAIGHT to a terminal status was never timed. Its start is
                // being invented here, in the same tick as its finish, so the elapsed time it
                // would report is zero — and a zero is not a fast task, it is an unmeasured one.
                //
                // Thirty of the thirty-five actuals in the estate are this: an engineer working
                // down a runbook and ticking each step off as they finish it, producing a table
                // of zero-minute tasks that would drag every average and every proposed RTO
                // toward nothing while looking exactly like data. The duration is now left
                // unset and the row is marked unmeasured, so a statistic can exclude it and
                // still count it in coverage.
                const wasTimed = !!task.actualStart
                const isTerminal = value === 'Completed' || value === 'Issue'
                if (!task.actualStart && (value === 'Started' || isTerminal)) {
                    task.actualStart = now.toISOString()
                }
                if (isTerminal) {
                    task.actualFinish = now.toISOString()
                    task.durationMeasured = wasTimed
                    task.actualDuration = wasTimed
                        ? minutesBetween(new Date(task.actualStart!), now)
                        : null
                    if (value === 'Issue') {
                        // Notify the AI assistant about the issue in background
                        onTaskIssue?.(task)
                    }
                }
            }
            updated[index] = task
            return updated
        })
    }, [readOnly, onTaskIssue])

    useEffect(() => {
        if (!hasEditsRef.current || dirtyIndices.size === 0) return
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = setTimeout(() => {
            const tasksToSave = localTasks
                .filter((t, idx) => t.id != null && dirtyIndices.has(idx))
                .map((task) => ({
                    id: task.id!,
                    ...(task.taskStatus !== undefined && { taskStatus: task.taskStatus }),
                    ...(task.taskOwner !== undefined && { taskOwner: task.taskOwner }),
                    ...(task.actualStart !== undefined && task.actualStart != null && { actualStart: task.actualStart }),
                    ...(task.actualFinish !== undefined && task.actualFinish != null && { actualFinish: task.actualFinish }),
                    ...(task.actualDuration !== undefined && task.actualDuration != null && { actualDuration: task.actualDuration }),
                    // CARRIED EXPLICITLY. This map is a hand-written allowlist, so a field added
                    // to the task object anywhere else is silently dropped here — which is what
                    // happened to durationMeasured: it was being set correctly on the task and
                    // never reaching the server, so DurationMeasured kept its DEFAULT 1 and the
                    // unmeasured task still read as a real zero-minute measurement.
                    // `false` is the whole point of the field, so the guard tests for undefined
                    // rather than falsiness.
                    ...(task.durationMeasured !== undefined && { durationMeasured: task.durationMeasured }),
                    ...(task.duration != null && { duration: task.duration }),
                }))
                .filter((t) => Object.keys(t).length > 1)
            if (tasksToSave.length > 0) {
                onSaveRef.current(tasksToSave)
                // Arm the post-save re-sync guard only when a save was actually
                // dispatched — otherwise no refetch follows and the guard would
                // linger. Capture the LIVE pre-save server reference so the sync
                // effect releases on the NEXT refetch, even across rapid edits.
                awaitingRefetchRef.current = true
                preSaveTasksRef.current = tasksRef.current
            }
            setDirtyIndices(new Set())
            hasEditsRef.current = false
        }, 500)
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        }
    }, [localTasks, dirtyIndices])

    const tasksWithIndex = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return []
        return localTasks.map((task, index) => ({ ...task, _index: index }))
    }, [localTasks])

    useEffect(() => {
        onPauseRefetchChange?.(dirtyIndices.size > 0 || dropdownOpen)
    }, [dirtyIndices.size, dropdownOpen, onPauseRefetchChange])

    const handleDropdownOpenChange = useCallback((open: boolean, rowIndex?: number) => {
        setDropdownOpen(open)
        setDropdownOpenRowIndex(open && rowIndex != null ? rowIndex : null)
    }, [])

    const handleRowExpand = useCallback((index: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev)
            if (next.has(index)) next.delete(index)
            else next.add(index)
            return next
        })
    }, [])

    const columns = useMemo(
        () =>
            createRunbookRecoveryColumns(
                handleTaskChange,
                t,
                i18n.language,
                forecastStartTime,
                tasksWithIndex,
                showFullTasks,
                handleDropdownOpenChange,
                readOnly
            ),
        [handleTaskChange, t, i18n.language, forecastStartTime, tasksWithIndex, showFullTasks, handleDropdownOpenChange, readOnly]
    )

    const columnVisibility = useMemo(
        () => ({
            taskOffset: false,
            forecastTaskOffset: false,
            ...(showActualsAndForecast
                ? {}
                : {
                      actualStart: false,
                      actualFinish: false,
                      actualDuration: false,
                      forecastStart: false,
                      forecastFinish: false,
                      forecastDuration: false,
                  }),
        }),
        [showActualsAndForecast]
    )

    const table = useReactTable({
        data: tasksWithIndex,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: { columnVisibility },
        onColumnVisibilityChange: () => {},
    })

    const task1 = useMemo(
        () =>
            tasksWithIndex.length > 0
                ? tasksWithIndex.find((t) => Number(t.taskNo) === 1) ??
                  tasksWithIndex.reduce(
                      (a, b) => ((a.taskOffset ?? 9999) < (b.taskOffset ?? 9999) ? a : b),
                      tasksWithIndex[0]
                  )
                : null,
        [tasksWithIndex]
    )

    const forecastBaseMs = useMemo(() => {
        if (task1?.actualStart) {
            return truncateToMinuteFloor(new Date(task1.actualStart)).getTime()
        }
        return truncateToMinuteFloor(new Date()).getTime()
    }, [task1?.actualStart])

    const forecastTimesMap = useMemo(
        () => computeForecastTimesMap(tasksWithIndex, forecastBaseMs),
        [tasksWithIndex, forecastBaseMs]
    )

    const readyTasks = useMemo(
        () =>
            tasksWithIndex.filter((t) => {
                const status = (t.taskStatus || 'Pending').toLowerCase()
                const waitGo = (t.waitGo || 'WAIT').toUpperCase()
                return status === 'pending' && waitGo === 'GO'
            }),
        [tasksWithIndex]
    )

    const automationCounts = useMemo(() => {
        let assisted = 0
        let agent = 0
        let armed = 0
        for (const task of tasksWithIndex) {
            const mode = taskAutomation(task)
            if (mode === 'assisted') assisted++
            else if (mode === 'agent') agent++
            if (isArmed(task)) armed++
        }
        return { assisted, agent, armed, total: assisted + agent }
    }, [tasksWithIndex])

    if (tasksWithIndex.length === 0) {
        return (
            <div className="rounded-lg border p-8 text-center text-muted-foreground">
                {t('recoverRequests.noRunbook')}
            </div>
        )
    }

    return (
        <div className="mt-6 space-y-4">
            {/* Decisions this recovery still holds — renders nothing when there are none. */}
            <RecoveryOptionsPanel requestID={requestID} readOnly={readOnly} />

            {/* View toggle: Card | Table */}
            <div className="flex flex-wrap items-center gap-4">
                <CardTableViewToggle
                    value={viewMode}
                    onValueChange={setViewMode}
                    cardLabel={t('objects.runbook.viewCard') || 'Card'}
                    tableLabel={t('objects.runbook.viewTable') || 'Table'}
                />
                {viewMode === 'table' && (
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Switch
                                id="show-full-tasks"
                                checked={showFullTasks}
                                onCheckedChange={setShowFullTasks}
                            />
                            <Label htmlFor="show-full-tasks" className="text-sm cursor-pointer">
                                {t('objects.runbook.showFullTasks') || 'Show full tasks'}
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                id="show-actuals-forecast"
                                checked={showActualsAndForecast}
                                onCheckedChange={setShowActualsAndForecast}
                            />
                            <Label htmlFor="show-actuals-forecast" className="text-sm cursor-pointer">
                                {t('recoverRequests.showActualsAndForecast') || 'Show actuals & forecast'}
                            </Label>
                        </div>
                    </div>
                )}
            </div>

            {automationCounts.total > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-purple-200 bg-purple-50/70 px-4 py-2.5">
                    <span className="text-sm font-semibold text-purple-800">
                        {t('objects.runbook.automationInRunbook') || 'Automation in this runbook'}
                    </span>
                    {automationCounts.agent > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-purple-700">
                            <Bot className="h-4 w-4" /> {automationCounts.agent} {t('objects.runbook.modeAgent')}
                        </span>
                    )}
                    {automationCounts.assisted > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-purple-700">
                            <Wand2 className="h-4 w-4" /> {automationCounts.assisted} {t('objects.runbook.assisted') || 'assisted'}
                        </span>
                    )}
                    {automationCounts.armed > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-900">
                            <span className="inline-block h-2 w-2 rounded-full bg-purple-500 animate-pulse" aria-hidden />
                            {automationCounts.armed} {t('objects.runbook.armedReady') || 'armed & ready'}
                        </span>
                    )}
                </div>
            )}

            {/* Pre-Signed URLs — collapsible card */}
            <div style={{ maxWidth: '900px' }}>
                <VersionUrlsTable requestID={requestID} />
            </div>

            {viewMode === 'table' ? (
                <DropdownOpenRowIndexContext.Provider value={dropdownOpenRowIndex}>
                    <ExpandedRowsContext.Provider value={expandedRows}>
                        <div className="rounded-md border overflow-x-auto runbook-recovery-table-wrapper">
                            <Table
                                className="w-full"
                                style={{
                                    tableLayout: 'fixed',
                                    minWidth: table.getVisibleLeafColumns().reduce((sum, col) => sum + (col.id === 'task' ? 320 : col.id === 'commands' ? 200 : col.getSize()), 0),
                                }}
                            >
                                <colgroup>
                                    {table.getVisibleLeafColumns().map((column) => {
                                        const w = column.id === 'task' ? 320 : column.id === 'commands' ? 200 : column.getSize()
                                        return <col key={column.id} style={{ width: w }} />
                                    })}
                                </colgroup>
                                <TableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id} className="bg-muted/50 border-b">
                                            {headerGroup.headers.map((header) => (
                                                <TableHead
                                                    key={header.id}
                                                    className="font-semibold text-foreground text-xs"
                                                    style={{
                                                        paddingTop: 'var(--table-header-padding-y, 0.75rem)',
                                                        paddingBottom: 'var(--table-header-padding-y, 0.75rem)',
                                                        paddingLeft: 'var(--table-header-padding-x, 1rem)',
                                                        paddingRight: 'var(--table-header-padding-x, 1rem)',
                                                        width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined,
                                                        minWidth: header.column.id === 'task' ? 320 : header.column.id === 'commands' ? 200 : undefined,
                                                    }}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {table.getRowModel().rows?.length ? (
                                        table.getRowModel().rows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                className="hover:bg-muted/50 cursor-pointer"
                                                onClick={(e) => {
                                                    if ((e.target as HTMLElement).closest('button, [role="combobox"], [data-radix-collection-item]')) return
                                                    handleRowExpand(row.original._index)
                                                }}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell
                                                        key={cell.id}
                                                        style={{
                                                            paddingTop: 'var(--table-cell-padding-y, 0.5rem)',
                                                            paddingBottom: 'var(--table-cell-padding-y, 0.5rem)',
                                                            paddingLeft: 'var(--table-header-padding-x, 1rem)',
                                                            paddingRight: 'var(--table-header-padding-x, 1rem)',
                                                            width: cell.column.getSize() !== 150 ? `${cell.column.getSize()}px` : undefined,
                                                            minWidth: cell.column.id === 'task' ? 320 : cell.column.id === 'commands' ? 200 : undefined,
                                                            overflow: 'visible',
                                                            verticalAlign: 'top',
                                                        }}
                                                        className="overflow-visible"
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                                {t('common.noResults')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                        <style>{`
                            .runbook-recovery-table-wrapper table { table-layout: fixed !important; }
                        `}</style>
                    </ExpandedRowsContext.Provider>
                </DropdownOpenRowIndexContext.Provider>
            ) : (
                <div className="max-w-3xl">
            {readyTasks.length > 0 && (
                <div className="rounded-xl border-2 border-purple-300 bg-purple-50/80 p-4 shadow-md shadow-purple-100 mb-8">
                    <h3 className="text-sm font-semibold text-purple-800 mb-3">
                        {readyTasks.length === 1
                            ? t('objects.runbook.taskReady') || 'Task Ready'
                            : `${t('objects.runbook.readyToStart') || 'Tasks Ready'} (${readyTasks.length} tasks)`}
                    </h3>
                    {readyTasks.length <= 3 ? (
                        <div className="space-y-2">
                            {readyTasks.map((task) => {
                                const idx = task._index
                                return (
                                    <div
                                        key={task.id ?? idx}
                                        className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-white border-2 border-purple-200"
                                    >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500 text-white text-xs font-semibold flex items-center justify-center">
                                                {task.taskNo ?? '-'}
                                            </span>
                                            <span className="text-sm text-foreground truncate">
                                                <TranslatableText text={task.task || t('objects.runbook.task')} />
                                            </span>
                                        </div>
                                        {!readOnly && (
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {(task.mode === 'assisted' || task.mode === 'agent') && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="border-purple-300 text-purple-700 hover:bg-purple-100"
                                                        onClick={() => openAutomate(task)}
                                                    >
                                                        <Play className="h-3.5 w-3.5 mr-1" />
                                                        {t('objects.runbook.automate') || 'Automate'}
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    className="bg-purple-600 hover:bg-purple-700"
                                                    onClick={() =>
                                                        handleTaskChange(idx, 'taskStatus', 'Started')
                                                    }
                                                >
                                                    {t('objects.runbook.startTask') || 'Start'}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-sm text-purple-800">
                                {readyTasks.length} tasks ready to start
                            </p>
                            {!readOnly && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                                    onClick={() => {
                                        const firstReadyIdx = tasksWithIndex.findIndex(
                                            (t) =>
                                                (t.taskStatus || 'Pending').toLowerCase() ===
                                                    'pending' &&
                                                (t.waitGo || 'WAIT').toUpperCase() === 'GO'
                                        )
                                        if (firstReadyIdx >= 0) {
                                            setExpandedRows((prev) => new Set(prev).add(firstReadyIdx))
                                            document
                                                .getElementById(`timeline-task-${firstReadyIdx}`)
                                                ?.scrollIntoView({ behavior: 'smooth' })
                                        }
                                    }}
                                >
                                    {t('objects.runbook.viewAll') || 'View All'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="relative pl-6">
                {/* Timeline line - centered through nodes (pl-6=24px + node radius 20px = 44px) */}
                <div
                    className="absolute left-[43px] top-5 bottom-5 w-0.5 bg-gray-200"
                    aria-hidden
                />

                <div className="space-y-4">
                    {tasksWithIndex.map((task, idx) => {
                        const isExpanded =
                            expandedRows.has(idx) || dropdownOpenRowIndex === idx
                        const taskNo = task.taskNo != null ? Number(task.taskNo) : null
                        const forecast = taskNo != null ? forecastTimesMap.get(taskNo) : undefined
                        const forecastFinishMs = forecast?.forecastFinishMs ?? null
                        return (
                            <div
                                key={task.id ?? idx}
                                id={`timeline-task-${idx}`}
                                className="relative flex gap-4 items-start"
                            >
                                <TimelineNode
                                    task={task}
                                    index={idx}
                                    onTaskChange={handleTaskChange}
                                    t={t}
                                    readOnly={readOnly}
                                />
                                <div className="flex-1 min-w-0">
                                    <TaskCard
                                        task={task}
                                        index={idx}
                                        isExpanded={isExpanded}
                                        onExpand={() => handleRowExpand(idx)}
                                        onTaskChange={handleTaskChange}
                                        onAutomate={canAutomate ? openAutomate : undefined}
                                        onDropdownOpenChange={handleDropdownOpenChange}
                                        forecastFinishMs={forecastFinishMs}
                                        copyToClipboard={copyToClipboard}
                                        copiedId={copiedId}
                                        allTasks={tasksWithIndex}
                                        onComment={onComment}
                                        t={t}
                                        readOnly={readOnly}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
                </div>
            )}
            <AutomateTaskDialog
                task={automateTask}
                requestID={requestID}
                open={automateOpen}
                onOpenChange={setAutomateOpen}
                onExecuted={() => {
                    // Fired successfully — the work now runs out-of-band (bridge/manifest),
                    // so move the task to In progress; the engineer marks it complete when done.
                    if (!automateTask || readOnly) return
                    const idx = localTasks.findIndex((tk) => tk.id === automateTask.id)
                    if (idx < 0) return
                    const status = (localTasks[idx].taskStatus || 'Pending').toLowerCase()
                    if (status !== 'completed' && status !== 'started') {
                        handleTaskChange(idx, 'taskStatus', 'Started')
                    }
                }}
            />
        </div>
    )
}
